import { Checkbox, Modal, Input, Select, message } from 'antd';
import React, { useMemo, useState, useRef } from 'react';
import { RejectCompressorContainer, RejectReasonContainer, RejectRecommendationContainer } from './styled';
import { RejectionCause, RejectionReason } from '@/store/Engine/constants';
import { getUIRejectionCauseLabel, getUIRejectReasonLabel } from '@/store/Engine/utils';
import { useStores } from '@/hooks/use-stores';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EPSelect } from '@/components';
import { recommendationModalConstants } from '@/constants/recommendalModal';
import {
  ModalState,
  initialState,
  checkCompressorReason,
  checkCompressorCause,
  checkTableCause,
  checkFreeTextReason,
  checkFreeTextCause,
  validateRejectionData,
} from './utils';

const { TextArea } = Input;

interface RejectRecommendationModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const RejectRecommendationModal = ({ isOpen, setIsOpen }: RejectRecommendationModalProps) => {
  const { engineStore, devicesStore } = useStores();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const compressors = useMemo(() => devicesStore.compressors, [devicesStore.compressors]);
  const recommendationTables = useMemo(
    () => JSON.parse(JSON.stringify(engineStore.currentRecommendationSetpointConfigurations)),
    [engineStore.currentRecommendationSetpointConfigurations],
  );

  const uniqueRecommendationTables: string[] = useMemo(
    () => Array.from(new Set(recommendationTables.map(item => item.name))),
    [recommendationTables],
  );

  const [state, setState] = useState<ModalState>(initialState);
  const textAreaRef = useRef(null);
  const possibleCauseTextAreaRef = useRef(null);
  const selectedReasonRef = useRef(null);
  const selectedPossibleCauseRef = useRef(null);
  const selectedCompressorRef = useRef(null);
  const selectedPossibleCauseCompressorsRef = useRef(null);
  const selectedPossibleCauseTablesRef = useRef<string[] | null>(null);

  const reasons = Object.values(RejectionReason).map(key => ({
    value: key,
    label: getUIRejectReasonLabel(key),
  }));

  const rejectionPossibleCauses = Object.values(RejectionCause).map(key => ({
    value: key,
    label: getUIRejectionCauseLabel(key),
  }));

  const compressorOptions = compressors.map(compressor => ({
    value: compressor.id,
    label: compressor.name,
  }));

  const optionsWithAll = [
    { label: t('All'), value: recommendationModalConstants.SELECT_ALL },
    ...uniqueRecommendationTables.map(name => ({
      label: name,
      value: name,
    })),
  ];

  const updateState = (updates: Partial<ModalState>) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  const onCancelClick = () => {
    setIsOpen(false);
    selectedReasonRef.current = null;
    selectedCompressorRef.current = null;
    selectedPossibleCauseRef.current = null;
    selectedPossibleCauseCompressorsRef.current = null;
    selectedPossibleCauseTablesRef.current = null;
    setState(initialState);
  };

  const handleSelectChange = (value: RejectionReason) => {
    selectedReasonRef.current = value;
    updateState({
      ...checkCompressorReason(value),
      ...checkFreeTextReason(value),
    });
  };

  const handleCauseSelectChange = (value: RejectionCause) => {
    selectedPossibleCauseRef.current = value;
    updateState({
      ...checkCompressorCause(value),
      ...checkFreeTextCause(value),
      ...checkTableCause(value),
      selectedTable: [],
    });
  };

  const handleCheckboxChange = (checkedValues: string[]) => {
    selectedCompressorRef.current = checkedValues;
    updateState({
      selectedCompressors: checkedValues || [],
      isReasonCompressorNotSelected: false,
    });
  };

  const handleCauseCheckboxChange = (checkedValues: string[]) => {
    selectedPossibleCauseCompressorsRef.current = checkedValues;
    updateState({
      selectedPossibleCauseCompressors: checkedValues || [],
      isCauseCompressorNotSelected: false,
    });
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateState({
      isError: false,
      commentText: e.target.value,
    });
  };

  const handleCauseTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateState({
      possibleCauseError: false,
      causeCommentText: e.target.value,
    });
  };

  const handleTableChange = (selectedValues: string[]) => {
    const hasSelectAll = selectedValues.includes(recommendationModalConstants.SELECT_ALL);
    const isAllSelected = selectedValues.length === uniqueRecommendationTables.length;

    const newSelection = hasSelectAll ? (isAllSelected ? [] : uniqueRecommendationTables) : selectedValues;

    updateState({
      selectedTable: newSelection,
      isTableNotSelected: false,
    });
    selectedPossibleCauseTablesRef.current = newSelection;
  };

  const submitRejectionData = async () => {
    const reason = selectedReasonRef.current;
    const cause = selectedPossibleCauseRef.current;
    const comment = state.commentText.trim();
    const causeComment = state.causeCommentText.trim();
    const compressors = state.selectedCompressors;
    const possibleCauseCompressors = state.selectedPossibleCauseCompressors;
    const possibleCauseTables = selectedPossibleCauseTablesRef.current || [];

    const validations = validateRejectionData(reason, cause, state, textAreaRef, possibleCauseTextAreaRef);

    const firstError = validations.find(v => !v.isValid);
    if (firstError) {
      if (firstError.stateUpdates) {
        updateState(firstError.stateUpdates);
      }

      if (firstError.focusRef?.current) {
        firstError.focusRef.current.focus();
      }

      message.error(t(firstError.errorMessage || 'validationError'));
      return;
    }

    await engineStore.rejectRecommendation({
      reason,
      comment,
      compressors,
      possibleCause: cause,
      commentForPossibleCause: causeComment,
      possibleCauseCompressors,
      possibleCauseTables,
    });

    setIsOpen(false);
    navigate(0);
  };

  return (
    <Modal
      destroyOnClose
      open={isOpen}
      centered
      closable
      onCancel={onCancelClick}
      onOk={submitRejectionData}
      width={800}
    >
      <RejectRecommendationContainer>
        <RejectReasonContainer>
          <p>{t('Reason:')}</p>
          <EPSelect onChange={handleSelectChange} allowClear placeholder={t('SelectReason')} options={reasons} />
        </RejectReasonContainer>

        {state.showCompressor && (
          <RejectCompressorContainer>
            <p>{t('Compressor')}</p>
            <Checkbox.Group
              options={compressorOptions}
              onChange={handleCheckboxChange}
              value={state.selectedCompressors}
              className={state.isReasonCompressorNotSelected ? 'error' : ''}
            />
          </RejectCompressorContainer>
        )}

        {state.showFreeText && (
          <div className="comments-container">
            <p>{t('comments')}</p>
            <TextArea
              onChange={handleTextAreaChange}
              value={state.commentText}
              placeholder={t('provideReason')}
              autoSize={{ minRows: 3, maxRows: 5 }}
              ref={textAreaRef}
              className={state.isError ? 'error' : ''}
              maxLength={200}
              minLength={2}
            />
            {state.isError && <span className="error-message">{t('provideReason')}</span>}
          </div>
        )}

        <RejectReasonContainer>
          <p>{t('possiblecause')}</p>

          <EPSelect
            onChange={handleCauseSelectChange}
            allowClear
            placeholder={t('SelectReason')}
            options={rejectionPossibleCauses}
          />
        </RejectReasonContainer>
        {state.showPossibleCauseCompressor && (
          <RejectCompressorContainer>
            <p>{t('Compressor')}</p>
            <Checkbox.Group
              options={compressorOptions}
              onChange={handleCauseCheckboxChange}
              value={state.selectedPossibleCauseCompressors}
              className={state.isCauseCompressorNotSelected ? 'error' : ''}
            />
          </RejectCompressorContainer>
        )}

        {state.showPossibleCauseFreeText && (
          <div className="comments-container">
            <p>{t('comments')}</p>
            <TextArea
              onChange={handleCauseTextAreaChange}
              value={state.causeCommentText}
              placeholder={t('provideReason')}
              autoSize={{ minRows: 3, maxRows: 5 }}
              ref={possibleCauseTextAreaRef}
              className={state.possibleCauseError ? 'error' : ''}
              maxLength={200}
              minLength={2}
            />
            {state.possibleCauseError && <span className="error-message">{t('provideReason')}</span>}
          </div>
        )}

        {state.showTable && (
          <div className="table-selector">
            <label className="label">Tables:</label>
            <Select
              className={`table-dropdown ${state.isTableNotSelected ? 'error' : ''}`}
              mode="multiple"
              placeholder={t('selecttables')}
              style={{ minWidth: 250 }}
              value={
                state.selectedTable.length === uniqueRecommendationTables.length
                  ? [...state.selectedTable]
                  : state.selectedTable
              }
              onChange={handleTableChange}
              options={optionsWithAll}
              maxTagCount="responsive"
              allowClear
            />
          </div>
        )}
      </RejectRecommendationContainer>
    </Modal>
  );
};

export default RejectRecommendationModal;
